import { Tooltip } from "@xola/ui-kit";
import { useMemoizedFn } from "ahooks";
import React, { memo } from "react";
import { useMutation } from "react-query";
import { EquipmentUnit, EquipmentUnitStatus, Experience, ExperiencePurchaseItem, Purchase } from "types";
import { api } from "../../../api/api";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useResource } from "../../../hooks/useResource";
import { updateTimeslotCache } from "../../Event/EditEventCapacityModal";
import { CheckInButton } from "./CheckInButton";

interface CheckInEquipmentUnitMenuProps {
    purchase: Purchase;
    purchaseItem: ExperiencePurchaseItem;
    equipment?: EquipmentUnit;
}

const NO_SHOW_STATUSES = new Set([EquipmentUnitStatus.PENDING_NO_SHOW, EquipmentUnitStatus.NO_SHOW]);

const isReserved = (unit: EquipmentUnit) => unit.checkInStatus === EquipmentUnitStatus.RESERVED;
const isCheckedOut = (unit: EquipmentUnit) => unit.checkInStatus === EquipmentUnitStatus.CHECKED_OUT;
const isReturned = (unit: EquipmentUnit) => unit.checkInStatus === EquipmentUnitStatus.RETURNED;
const isNoShow = (unit: EquipmentUnit) => NO_SHOW_STATUSES.has(unit.checkInStatus);

export const CheckInEquipmentUnitMenu = memo(({ purchase, purchaseItem, equipment }: CheckInEquipmentUnitMenuProps) => {
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();
    const { data: experience } = useResource<Experience>(["products", purchaseItem.product.id]);

    const allowCheckout = experience?.allowCheckoutAtArrival ?? false;
    const trackReturns = experience?.earlyReturn ?? false;

    const targetUnits = equipment
        ? [equipment]
        : (purchaseItem.equipments ?? []).filter((unit) => unit.checkInStatus !== EquipmentUnitStatus.CANCELED);

    const { mutateAsync: saveStatus, isLoading: isSaving } = useMutation(
        async ({ units, checkInStatus }: { units: EquipmentUnit[]; checkInStatus: EquipmentUnitStatus }) => {
            await api.patch(`/purchases/${purchase.id}`, {
                items: [
                    {
                        id: purchaseItem.id,
                        equipments: units.map((unit) => ({ id: unit.id, checkInStatus })),
                    },
                ],
            });
            invalidate("purchases", purchase.id);
            invalidate("purchaseItems", purchaseItem.id);
            invalidate("events", purchaseItem.event.id);
            invalidate("timeslots");
            setTimeout(() => {
                updateTimeslotCache(
                    {
                        timeslot: null,
                        event: null,
                        productId: purchaseItem.product.id,
                        startDate: purchaseItem.arrivalDate,
                    },
                    { isGranted },
                );
            }, 2000);
        },
    );

    const toggle = (
        doneUnits: EquipmentUnit[],
        pendingUnits: EquipmentUnit[],
        doneStatus: EquipmentUnitStatus,
        pendingStatus: EquipmentUnitStatus,
    ) => {
        const active = pendingUnits.length === 0 && doneUnits.length > 0;
        return {
            active,
            units: active ? doneUnits : pendingUnits,
            checkInStatus: active ? pendingStatus : doneStatus,
        };
    };

    const checkedOutUnits = targetUnits.filter((unit) => isCheckedOut(unit));
    const returnedUnits = targetUnits.filter((unit) => isReturned(unit));
    const checkoutUnits = targetUnits.filter((unit) => isReserved(unit) || isNoShow(unit));
    const isReturnMode =
        trackReturns && checkoutUnits.length === 0 && (checkedOutUnits.length > 0 || returnedUnits.length > 0);
    const primary = isReturnMode
        ? toggle(returnedUnits, checkedOutUnits, EquipmentUnitStatus.RETURNED, EquipmentUnitStatus.CHECKED_OUT)
        : toggle(checkedOutUnits, checkoutUnits, EquipmentUnitStatus.CHECKED_OUT, EquipmentUnitStatus.RESERVED);

    const noShow = toggle(
        targetUnits.filter((unit) => isNoShow(unit)),
        targetUnits.filter((unit) => isReserved(unit) || isCheckedOut(unit)),
        EquipmentUnitStatus.PENDING_NO_SHOW,
        EquipmentUnitStatus.RESERVED,
    );

    const handlePrimaryClick = useMemoizedFn(async () => {
        await saveStatus({ units: primary.units, checkInStatus: primary.checkInStatus });
    });

    const handleNoShowClick = useMemoizedFn(async () => {
        await saveStatus({ units: noShow.units, checkInStatus: noShow.checkInStatus });
    });

    const label = equipment ? "" : " all equipments";
    const primaryTooltip = isReturnMode ? `Return ${label}` : `Checkout${label}`;

    if (!allowCheckout) {
        return null;
    }
    return (
        <span className="flex">
            <Tooltip content={primaryTooltip}>
                <CheckInButton
                    className="!mr-2"
                    action={isReturnMode ? "equipmentReturn" : "equipmentCheckout"}
                    isDisabled={isSaving}
                    active={primary.active}
                    isLoading={isSaving}
                    onClick={handlePrimaryClick}
                />
            </Tooltip>

            <Tooltip content={`No-show${label}`}>
                <CheckInButton
                    action="noShow"
                    isDisabled={isSaving}
                    active={noShow.active}
                    isLoading={isSaving}
                    onClick={handleNoShowClick}
                />
            </Tooltip>
        </span>
    );
});

CheckInEquipmentUnitMenu.displayName = "CheckInEquipmentUnitMenu";
